// @ts-nocheck - Large file, minimal TS migration
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  useGetTagsQuery,
} from '../../services/tabbedDictApiSlice';
import { useIsAdmin } from '../../features/auth/authHooks';
import { X, Search, FileText, Plus, Minus, Save, Pencil, Trash2, ArrowLeft, ChevronRight, Database, User} from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import Button from '../Button/Button';
import {
  useGetUidDictQuery,
  useLazyGetUidDictQuery,
  useLazyGetSearchableListQuery,
  useUpdateUidDictMutation,
  useDeleteUidDictMutation
} from '../../services/uidDictApiSlice';

const DATA_CRUD_EXCLUDED_FIELDS = ['parentDocument', 'subcollection', 'uid', 'id', 'description', 'created_at', 'updated_at', 'type', 'note', 'customFields', 'crudMode', 'creationLevel'];

export const UsersList = forwardRef((_, ref) => {
  const { data: users = [], isLoading, error } = useGetUidDictQuery({
    collection: 'tabbed_dict',
    documents: 'admin'
  });
  useImperativeHandle(ref, () => ({
    getUsers: () => users
  }));
  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  return (
    <div>
      {users.map((user) => (
        <div key={user.id}>{user.username} {JSON.stringify(user.roles)}</div>
      ))}
    </div>
  );
});
// export default forwardRef(UsersList);

// Document Form Popup Component
const DocumentForm = ({ isOpen, onClose, onSubmit, initialData = {}, theme, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    parentDocument: '',
    subcollection: '',
    uid: '',
    admin: false,
    editor: false,
    username: '',
    description: '',
    ...initialData
  });

  const [crudMode, setCrudMode] = useState(''); // 'UserCRUD' or 'DataCRUD'
  const [creationLevel, setCreationLevel] = useState('full'); // 'parent', 'subcollection', 'full'
  const [customFields, setCustomFields] = useState([{ name: '', value: '' }]); // For DataCRUD dynamic fields

  const normalizeCustomFields = (value) => {
    if (Array.isArray(value)) {
      const validArray = value.filter((field) => field?.name);
      return validArray.length > 0 ? validArray : [{ name: '', value: '' }];
    }

    if (value && typeof value === 'object') {
      const mappedFields = Object.entries(value).map(([name, fieldValue]) => ({
        name,
        value: String(fieldValue),
      }));
      return mappedFields.length > 0 ? mappedFields : [{ name: '', value: '' }];
    }

    return [{ name: '', value: '' }];
  };

  useEffect(() => {
    if (isOpen) {
      // Check if this is update mode (initialData has existing document data)
      const isUpdateMode = mode === 'update' && initialData && Object.keys(initialData).length > 0;

      if (isUpdateMode) {
        // Extract the actual document data (it's nested in initialData.data)
        const documentData = initialData.data || initialData;

        // Ensure documentData is an object
        if (!documentData || typeof documentData !== 'object') {
          console.warn('Invalid document data for update mode:', documentData);
          return;
        }

        // Get metadata from initialData
        const parentDocument = initialData.parentDocument || '';
        const subcollection = initialData.subcollection || '';
        const uid = initialData.id || documentData.uid || documentData.id || '';

        // Determine CRUD mode based on data structure
        const hasUserCRUDFields = 'username' in documentData || 'admin' in documentData || 'editor' in documentData;
        const detectedCrudMode = hasUserCRUDFields ? 'UserCRUD' : 'DataCRUD';

        // Extract base fields
        const baseData = {
          parentDocument,
          subcollection,
          uid,
          description: documentData.description || '',
        };

        if (detectedCrudMode === 'UserCRUD') {
          // UserCRUD: Set role fields
          setFormData({
            ...baseData,
            admin: documentData.admin || false,
            editor: documentData.editor || false,
            username: documentData.username || '',
          });
          setCustomFields([{ name: '', value: '' }]);
        } else {
          // DataCRUD: Extract custom fields
          const customFieldsData = Object.entries(documentData)
            .filter(([key]) => !DATA_CRUD_EXCLUDED_FIELDS.includes(key))
            .map(([name, value]) => ({ name, value: String(value) }));

          setFormData({
            ...baseData,
            admin: false,
            editor: false,
            username: '',
          });
          setCustomFields(customFieldsData.length > 0 ? customFieldsData : [{ name: '', value: '' }]);
        }

        setCrudMode(detectedCrudMode);
        setCreationLevel('full');
      } else {
        // Create mode: Reset to defaults
        const defaultCrudMode = initialData?.crudMode || '';
        const defaultCreationLevel = initialData?.creationLevel || 'full';

        setFormData({
          parentDocument: '',
          subcollection: '',
          uid: '',
          admin: false,
          editor: false,
          username: '',
          description: '',
          ...initialData
        });
        setCrudMode(defaultCrudMode);
        setCreationLevel(defaultCreationLevel);
        setCustomFields(normalizeCustomFields(initialData?.customFields));
      }
    }
  }, [isOpen, initialData, mode]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      creationLevel,
      crudMode,
      updatedAt: new Date().toISOString(),
      lastModified: `${mode === 'create' ? 'Created' : 'Updated'} via modal`
    };

    // If DataCRUD mode, add custom fields to submitData
    if (crudMode === 'DataCRUD' && creationLevel === 'full') {
      // Convert customFields array to object, filtering out empty field names
      const fieldsObject = customFields.reduce((acc, field) => {
        if (field.name.trim()) {
          acc[field.name] = field.value;
        }
        return acc;
      }, {});

      submitData.customFields = fieldsObject;
    }

    onSubmit(submitData);
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Custom field management for DataCRUD
  const addCustomField = () => {
    setCustomFields(prev => [...prev, { name: '', value: '' }]);
  };

  const removeCustomField = (index) => {
    setCustomFields(prev => prev.filter((_, i) => i !== index));
  };

  const updateCustomField = (index, field, value) => {
    setCustomFields(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
      <div
        className="w-full max-w-lg rounded-lg shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: theme.backgroundPrimary,
          color: theme.textPrimary
        }}
      >
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0"
             style={{
               borderColor: theme.borderColor,
               backgroundColor: theme.backgroundPrimary,
               zIndex: 10
             }}>
          <h3 className="text-lg font-semibold">
            {mode === 'create' ? 'Create Document' : 'Update Document'}
          </h3>
          <Button
            onClick={onClose}
            className="p-1 rounded-lg hover:opacity-70 transition-opacity"
            style={{ color: theme.textSecondary }}
          >
            <X size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* CRUD Mode Selector */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
              Select Operation Type
            </label>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setCrudMode('UserCRUD')}
                className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                  crudMode === 'UserCRUD' ? 'ring-2' : ''
                }`}
                style={{
                  backgroundColor: crudMode === 'UserCRUD' ? theme.primary : theme.backgroundSecondary,
                  borderColor: crudMode === 'UserCRUD' ? theme.primary : theme.borderColor,
                  color: crudMode === 'UserCRUD' ? '#fff' : theme.textPrimary,
                  '--tw-ring-color': theme.primary
                }}
              >
                👤 UserCRUD
              </Button>
              <Button
                type="button"
                onClick={() => setCrudMode('DataCRUD')}
                className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                  crudMode === 'DataCRUD' ? 'ring-2' : ''
                }`}
                style={{
                  backgroundColor: crudMode === 'DataCRUD' ? theme.primary : theme.backgroundSecondary,
                  borderColor: crudMode === 'DataCRUD' ? theme.primary : theme.borderColor,
                  color: crudMode === 'DataCRUD' ? '#fff' : theme.textPrimary,
                  '--tw-ring-color': theme.primary
                }}
              >
                📊 DataCRUD
              </Button>
            </div>
            <div className="text-xs mt-2 p-2 rounded" style={{ color: theme.textSecondary, backgroundColor: theme.backgroundSecondary }}>
              {!crudMode && 'Select an operation type to continue'}
              {crudMode === 'UserCRUD' && '👤 Manage users, roles, and permissions'}
              {crudMode === 'DataCRUD' && '📊 Manage data documents and content'}
            </div>
          </div>

          {/* Creation Level Selector - Only show for UserCRUD */}
          {crudMode === 'UserCRUD' && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
                What do you want to create?
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => setCreationLevel('parent')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    creationLevel === 'parent' ? 'font-medium' : ''
                  }`}
                  style={{
                    backgroundColor: creationLevel === 'parent' ? theme.primary : theme.backgroundSecondary,
                    borderColor: creationLevel === 'parent' ? theme.primary : theme.borderColor,
                    color: creationLevel === 'parent' ? '#fff' : theme.textPrimary
                  }}
                >
                  Structure
                </Button>
                <Button
                  type="button"
                  onClick={() => setCreationLevel('subcollection')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    creationLevel === 'subcollection' ? 'font-medium' : ''
                  }`}
                  style={{
                    backgroundColor: creationLevel === 'subcollection' ? theme.primary : theme.backgroundSecondary,
                    borderColor: creationLevel === 'subcollection' ? theme.primary : theme.borderColor,
                    color: creationLevel === 'subcollection' ? '#fff' : theme.textPrimary
                  }}
                >
                  + Subcollection
                </Button>
                <Button
                  type="button"
                  onClick={() => setCreationLevel('full')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    creationLevel === 'full' ? 'font-medium' : ''
                  }`}
                  style={{
                    backgroundColor: creationLevel === 'full' ? theme.primary : theme.backgroundSecondary,
                    borderColor: creationLevel === 'full' ? theme.primary : theme.borderColor,
                    color: creationLevel === 'full' ? '#fff' : theme.textPrimary
                  }}
                >
                  Full User
                </Button>
              </div>
              <div className="text-xs mt-2 p-2 rounded" style={{ color: theme.textSecondary, backgroundColor: theme.backgroundSecondary }}>
                {creationLevel === 'parent' && (
                  <div>
                    <div>📁 Creates parent document structure with <code className="px-1 rounded" style={{ backgroundColor: theme.backgroundPrimary }}>_init/{'{uuid}'}</code> placeholder</div>
                    <div className="mt-1 text-xs opacity-75">Firestore requires at least one document in subcollections. A unique ID will be auto-generated.</div>
                  </div>
                )}
                {creationLevel === 'subcollection' && (
                  <div>
                    <div>📂 Creates subcollection with auto-generated UUID placeholder document</div>
                    <div className="mt-1 text-xs opacity-75">You can delete the placeholder after adding real documents</div>
                  </div>
                )}
                {creationLevel === 'full' && '✅ Recommended: Create complete user document with all role fields'}
              </div>
            </div>
          )}

          {/* Creation Level Selector - Only show for DataCRUD */}
          {crudMode === 'DataCRUD' && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
                What do you want to create?
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => setCreationLevel('parent')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    creationLevel === 'parent' ? 'font-medium' : ''
                  }`}
                  style={{
                    backgroundColor: creationLevel === 'parent' ? theme.primary : theme.backgroundSecondary,
                    borderColor: creationLevel === 'parent' ? theme.primary : theme.borderColor,
                    color: creationLevel === 'parent' ? '#fff' : theme.textPrimary
                  }}
                >
                  Structure
                </Button>
                <Button
                  type="button"
                  onClick={() => setCreationLevel('subcollection')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    creationLevel === 'subcollection' ? 'font-medium' : ''
                  }`}
                  style={{
                    backgroundColor: creationLevel === 'subcollection' ? theme.primary : theme.backgroundSecondary,
                    borderColor: creationLevel === 'subcollection' ? theme.primary : theme.borderColor,
                    color: creationLevel === 'subcollection' ? '#fff' : theme.textPrimary
                  }}
                >
                  + Subcollection
                </Button>
                <Button
                  type="button"
                  onClick={() => setCreationLevel('full')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    creationLevel === 'full' ? 'font-medium' : ''
                  }`}
                  style={{
                    backgroundColor: creationLevel === 'full' ? theme.primary : theme.backgroundSecondary,
                    borderColor: creationLevel === 'full' ? theme.primary : theme.borderColor,
                    color: creationLevel === 'full' ? '#fff' : theme.textPrimary
                  }}
                >
                  Full Document
                </Button>
              </div>
              <div className="text-xs mt-2 p-2 rounded" style={{ color: theme.textSecondary, backgroundColor: theme.backgroundSecondary }}>
                {creationLevel === 'parent' && (
                  <div>
                    <div>📁 Creates parent document structure with <code className="px-1 rounded" style={{ backgroundColor: theme.backgroundPrimary }}>_init/{'{uuid}'}</code> placeholder</div>
                    <div className="mt-1 text-xs opacity-75">Firestore requires at least one document in subcollections. A unique ID will be auto-generated.</div>
                  </div>
                )}
                {creationLevel === 'subcollection' && (
                  <div>
                    <div>📂 Creates subcollection with auto-generated UUID placeholder document</div>
                    <div className="mt-1 text-xs opacity-75">You can delete the placeholder after adding real documents</div>
                  </div>
                )}
                {creationLevel === 'full' && '✅ Recommended: Create complete document with custom fields'}
              </div>
            </div>
          )}

          {crudMode && (
            <>
              <div className="h-px" style={{ backgroundColor: theme.borderColor }} />

              {/* Parent Document */}
              <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
              Parent Document <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-2">
              <Button
                type="button"
                onClick={() => handleInputChange('parentDocument', 'users')}
                className="px-3 py-1 rounded text-xs border transition-colors"
                style={{
                  backgroundColor: formData.parentDocument === 'users' ? theme.primary : theme.backgroundSecondary,
                  borderColor: theme.borderColor,
                  color: formData.parentDocument === 'users' ? '#fff' : theme.textSecondary
                }}
              >
                users
              </Button>
              <Button
                type="button"
                onClick={() => handleInputChange('parentDocument', 'documents')}
                className="px-3 py-1 rounded text-xs border transition-colors"
                style={{
                  backgroundColor: formData.parentDocument === 'documents' ? theme.primary : theme.backgroundSecondary,
                  borderColor: theme.borderColor,
                  color: formData.parentDocument === 'documents' ? '#fff' : theme.textSecondary
                }}
              >
                documents
              </Button>
              <Button
                type="button"
                onClick={() => handleInputChange('parentDocument', 'settings')}
                className="px-3 py-1 rounded text-xs border transition-colors"
                style={{
                  backgroundColor: formData.parentDocument === 'settings' ? theme.primary : theme.backgroundSecondary,
                  borderColor: theme.borderColor,
                  color: formData.parentDocument === 'settings' ? '#fff' : theme.textSecondary
                }}
              >
                settings
              </Button>
            </div>
            <input
              type="text"
              value={formData.parentDocument || ''}
              onChange={(e) => handleInputChange('parentDocument', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
              style={{
                backgroundColor: theme.backgroundSecondary,
                borderColor: theme.borderColor,
                color: theme.textPrimary,
                '--tw-ring-color': theme.primary
              }}
              placeholder="e.g., users, documents, settings"
              required
            />
            <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
              Path: tabbed_dict/<span className="font-mono" style={{ color: theme.primary }}>{formData.parentDocument || '[parent]'}</span>
            </div>
          </div>

          {/* Subcollection (show if subcollection or full) */}
          {(creationLevel === 'subcollection' || creationLevel === 'full') && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
                Subcollection <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 mb-2">
                <Button
                  type="button"
                  onClick={() => handleInputChange('subcollection', 'admin')}
                  className="px-3 py-1 rounded text-xs border transition-colors"
                  style={{
                    backgroundColor: formData.subcollection === 'admin' ? theme.primary : theme.backgroundSecondary,
                    borderColor: theme.borderColor,
                    color: formData.subcollection === 'admin' ? '#fff' : theme.textSecondary
                  }}
                >
                  admin
                </Button>
                <Button
                  type="button"
                  onClick={() => handleInputChange('subcollection', 'nonadmin')}
                  className="px-3 py-1 rounded text-xs border transition-colors"
                  style={{
                    backgroundColor: formData.subcollection === 'nonadmin' ? theme.primary : theme.backgroundSecondary,
                    borderColor: theme.borderColor,
                    color: formData.subcollection === 'nonadmin' ? '#fff' : theme.textSecondary
                  }}
                >
                  nonadmin
                </Button>
                <Button
                  type="button"
                  onClick={() => handleInputChange('subcollection', 'editors')}
                  className="px-3 py-1 rounded text-xs border transition-colors"
                  style={{
                    backgroundColor: formData.subcollection === 'editors' ? theme.primary : theme.backgroundSecondary,
                    borderColor: theme.borderColor,
                    color: formData.subcollection === 'editors' ? '#fff' : theme.textSecondary
                  }}
                >
                  editors
                </Button>
              </div>
              <input
                type="text"
                value={formData.subcollection || ''}
                onChange={(e) => handleInputChange('subcollection', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: theme.backgroundSecondary,
                  borderColor: theme.borderColor,
                  color: theme.textPrimary,
                  '--tw-ring-color': theme.primary
                }}
                placeholder="e.g., admin, nonadmin, editors"
                required
              />
              <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                Path: tabbed_dict/{formData.parentDocument || '[parent]'}/<span className="font-mono" style={{ color: theme.primary }}>{formData.subcollection || '[subcollection]'}</span>
              </div>
            </div>
          )}
            </>
          )}

          {/* User Document & Roles (show only for UserCRUD full creation) */}
          {crudMode === 'UserCRUD' && creationLevel === 'full' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
                  User ID (UID) <span className="text-xs" style={{ color: theme.textSecondary }}>(optional - auto-generates if empty)</span>
                </label>
                <input
                  type="text"
                  value={formData.uid || ''}
                  onChange={(e) => handleInputChange('uid', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 font-mono text-sm"
                  style={{
                    backgroundColor: theme.backgroundSecondary,
                    borderColor: theme.borderColor,
                    color: theme.textPrimary,
                    '--tw-ring-color': theme.primary
                  }}
                  placeholder="Leave empty to auto-generate UUID"
                />
                <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                  Full Path: tabbed_dict/{formData.parentDocument || '[parent]'}/{formData.subcollection || '[subcollection]'}/{formData.uid ? <span className="font-mono" style={{ color: theme.primary }}>{formData.uid}</span> : <span className="font-mono" style={{ color: '#9ca3af', fontStyle: 'italic' }}>{'{auto-generated-uuid}'}</span>}
                </div>
              </div>

              <div className="h-px" style={{ backgroundColor: theme.borderColor }} />

              <div className="space-y-3">
                <label className="block text-sm font-medium" style={{ color: theme.textPrimary }}>
                  Role Fields
                </label>

                {/* Username */}
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: theme.textSecondary }}>
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.username || ''}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: theme.backgroundSecondary,
                      borderColor: theme.borderColor,
                      color: theme.textPrimary,
                      '--tw-ring-color': theme.primary
                    }}
                    placeholder="Enter username"
                    required
                  />
                </div>

                {/* Admin Checkbox */}
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: theme.backgroundSecondary }}>
                  <input
                    type="checkbox"
                    id="admin-checkbox"
                    checked={formData.admin}
                    onChange={(e) => handleInputChange('admin', e.target.checked)}
                    className="w-5 h-5 rounded"
                    style={{ accentColor: theme.primary }}
                  />
                  <label htmlFor="admin-checkbox" className="flex-1 cursor-pointer">
                    <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>
                      Admin Privileges
                    </div>
                    <div className="text-xs" style={{ color: theme.textSecondary }}>
                      Full access to all features and settings
                    </div>
                  </label>
                </div>

                {/* Editor Checkbox */}
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: theme.backgroundSecondary }}>
                  <input
                    type="checkbox"
                    id="editor-checkbox"
                    checked={formData.editor}
                    onChange={(e) => handleInputChange('editor', e.target.checked)}
                    className="w-5 h-5 rounded"
                    style={{ accentColor: theme.primary }}
                  />
                  <label htmlFor="editor-checkbox" className="flex-1 cursor-pointer">
                    <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>
                      Editor Privileges
                    </div>
                    <div className="text-xs" style={{ color: theme.textSecondary }}>
                      Can edit and modify content
                    </div>
                  </label>
                </div>
              </div>

              {/* Description for UserCRUD */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
                  Description <span className="text-xs" style={{ color: theme.textSecondary }}>(optional)</span>
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 resize-none"
                  style={{
                    backgroundColor: theme.backgroundSecondary,
                    borderColor: theme.borderColor,
                    color: theme.textPrimary,
                    '--tw-ring-color': theme.primary
                  }}
                  placeholder="Optional description or notes"
                />
              </div>
            </>
          )}

          {/* Data Document & Custom Fields (show only for DataCRUD full creation) */}
          {crudMode === 'DataCRUD' && creationLevel === 'full' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
                  Document ID <span className="text-xs" style={{ color: theme.textSecondary }}>(optional - auto-generates if empty)</span>
                </label>
                <input
                  type="text"
                  value={formData.uid || ''}
                  onChange={(e) => handleInputChange('uid', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 font-mono text-sm"
                  style={{
                    backgroundColor: theme.backgroundSecondary,
                    borderColor: theme.borderColor,
                    color: theme.textPrimary,
                    '--tw-ring-color': theme.primary
                  }}
                  placeholder="Leave empty to auto-generate UUID"
                />
                <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                  Full Path: tabbed_dict/{formData.parentDocument || '[parent]'}/{formData.subcollection || '[subcollection]'}/{formData.uid ? <span className="font-mono" style={{ color: theme.primary }}>{formData.uid}</span> : <span className="font-mono" style={{ color: '#9ca3af', fontStyle: 'italic' }}>{'{auto-generated-uuid}'}</span>}
                </div>
              </div>

              <div className="h-px" style={{ backgroundColor: theme.borderColor }} />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium" style={{ color: theme.textPrimary }}>
                    Custom Fields
                  </label>
                  <Button
                    type="button"
                    onClick={addCustomField}
                    className="px-3 py-1 rounded-lg border text-xs font-medium transition-colors flex items-center gap-1"
                    style={{
                      backgroundColor: theme.backgroundSecondary,
                      borderColor: theme.borderColor,
                      color: theme.primary
                    }}
                  >
                    <Plus size={14} />
                    Add Field
                  </Button>
                </div>

                {customFields.map((field, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: theme.textSecondary }}>
                          Field Name
                        </label>
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => updateCustomField(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                          style={{
                            backgroundColor: theme.backgroundSecondary,
                            borderColor: theme.borderColor,
                            color: theme.textPrimary,
                            '--tw-ring-color': theme.primary
                          }}
                          placeholder="e.g., title, status"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: theme.textSecondary }}>
                          Field Value
                        </label>
                        <input
                          type="text"
                          value={field.value}
                          onChange={(e) => updateCustomField(index, 'value', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                          style={{
                            backgroundColor: theme.backgroundSecondary,
                            borderColor: theme.borderColor,
                            color: theme.textPrimary,
                            '--tw-ring-color': theme.primary
                          }}
                          placeholder="e.g., My Document"
                        />
                      </div>
                    </div>
                    {customFields.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeCustomField(index)}
                        className="mt-6 p-2 rounded-lg border transition-colors"
                        style={{
                          backgroundColor: theme.backgroundSecondary,
                          borderColor: theme.borderColor,
                          color: theme.textSecondary
                        }}
                        title="Remove field"
                      >
                        <Minus size={16} />
                      </Button>
                    )}
                  </div>
                ))}

                <div className="text-xs p-2 rounded" style={{ color: theme.textSecondary, backgroundColor: theme.backgroundSecondary }}>
                  💡 Add any custom fields and values for your document. Fields with empty names will be ignored.
                </div>
              </div>

              {/* Description for DataCRUD */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
                  Description <span className="text-xs" style={{ color: theme.textSecondary }}>(optional)</span>
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 resize-none"
                  style={{
                    backgroundColor: theme.backgroundSecondary,
                    borderColor: theme.borderColor,
                    color: theme.textPrimary,
                    '--tw-ring-color': theme.primary
                  }}
                  placeholder="Optional description or notes"
                />
              </div>
            </>
          )}

          {/* Path Preview */}
          {crudMode && (
            <div className="p-4 rounded-lg border" style={{ backgroundColor: theme.backgroundSecondary, borderColor: theme.borderColor }}>
              <div className="text-xs font-medium mb-2" style={{ color: theme.textSecondary }}>
                Firestore Path Preview:
              </div>
              <div className="font-mono text-sm break-all" style={{ color: theme.textPrimary }}>
                <span style={{ color: theme.textSecondary }}>tabbed_dict/</span>
                <span style={{ color: theme.primary, fontWeight: 'bold' }}>
                  {formData.parentDocument || '[parent]'}
                </span>
                {creationLevel === 'parent' && (
                  <>
                    <span style={{ color: theme.textSecondary }}>/</span>
                    <span style={{ color: '#6b7280', fontStyle: 'italic' }}>
                      _init/
                    </span>
                    <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.85em' }}>
                      {'{auto-generated-uuid}'}
                    </span>
                  </>
                )}
                {(creationLevel === 'subcollection' || creationLevel === 'full') && (
                  <>
                    <span style={{ color: theme.textSecondary }}>/</span>
                    <span style={{ color: theme.primary, fontWeight: 'bold' }}>
                      {formData.subcollection || '[subcollection]'}
                    </span>
                  </>
                )}
                {creationLevel === 'subcollection' && (
                  <>
                    <span style={{ color: theme.textSecondary }}>/</span>
                    <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.85em' }}>
                      {'{auto-generated-uuid}'}
                    </span>
                  </>
                )}
                {creationLevel === 'full' && (
                  <>
                    <span style={{ color: theme.textSecondary }}>/</span>
                    <span style={{ color: theme.primary, fontWeight: 'bold' }}>
                      {formData.uid || '[uid]'}
                    </span>
                  </>
                )}
              </div>
            </div>  
          )}
            {/* {creationLevel === 'full' && (
              <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: theme.borderColor, color: theme.textSecondary }}>
                <div className="font-medium mb-1">Document Fields:</div>
                <div className="space-y-1 font-mono">
                  <div>• username: <span style={{ color: theme.textPrimary }}>{formData.username || '(empty)'}</span></div>
                  <div>• admin: <span style={{ color: formData.admin ? '#10b981' : '#ef4444' }}>{formData.admin ? 'true' : 'false'}</span></div>
                  <div>• editor: <span style={{ color: formData.editor ? '#10b981' : '#ef4444' }}>{formData.editor ? 'true' : 'false'}</span></div>
                </div>
              </div>
            )} */}


          {crudMode && (
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg border transition-colors"
                style={{
                  borderColor: theme.borderColor,
                  color: theme.textSecondary
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 px-4 py-2 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
                style={{
                  backgroundColor: theme.primary,
                  color: '#fff'
                }}
              >
                <Save size={16} />
                {mode === 'create' ? 'Create' : 'Update'}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const SearchBarModal = ({ isOpen, onClose, theme }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [shouldSearch, setShouldSearch] = useState(false);
  const [formPopup, setFormPopup] = useState({ isOpen: false, mode: 'create', document: null });
  const { isAdmin } = useIsAdmin();
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [navigationStack, setNavigationStack] = useState([]); // Tracks navigation path
  const [currentLevel, setCurrentLevel] = useState('documents'); // 'documents', 'document', 'user', 'userData'
  const [currentData, setCurrentData] = useState(null); // Current level data
  const [, setLoadingUsersMap] = useState({});
  const [, setDocumentUsersMap] = useState({});
  const [isEditingRoles, setIsEditingRoles] = useState(false);
  const [editedRoles, setEditedRoles] = useState({ admin: false, editor: false, username: '' });
  const [editingUserLevelDocType, setEditingUserLevelDocType] = useState(null); // 'UserCRUD' or 'DataCRUD' for user level
  const [editedUserLevelDocData, setEditedUserLevelDocData] = useState({}); // For DataCRUD at user level
  const [editingUserId, setEditingUserId] = useState(null); // Track which user is being edited in document level
  const [editedUserRoles, setEditedUserRoles] = useState({ admin: false, editor: false, username: '' });
  const [editedDocData, setEditedDocData] = useState({}); // For DataCRUD document editing
  const [editingDocType, setEditingDocType] = useState(null); // 'UserCRUD' or 'DataCRUD'
  // const [selectedDocumentUsers, setSelectedDocumentUsers] = useState(null);

  // Autocomplete states
  const [allSuggestions, setAllSuggestions] = useState([]); // All documents and subcollections
  const [firestoreSearchableList, setFirestoreSearchableList] = useState([]);
  const [isLoadingSearchableList, setIsLoadingSearchableList] = useState(false);
  const searchableListLoadStartedRef = useRef(false);
  const searchInputRef = useRef(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]); // Results after search
  const [isSearchActive, setIsSearchActive] = useState(false); // Track if search has been executed

  const [triggerGetUsers] = useLazyGetUidDictQuery()
  const [triggerGetSearchableList] = useLazyGetSearchableListQuery()
  const [updateUidDict] = useUpdateUidDictMutation()
  const [deleteUidDict] = useDeleteUidDictMutation()

  // Sample data for demonstration
  const sampleDocuments = [
    {
      id: 'SDSgeneraluser',
      path: 'tabbed_dict/public/SDSgeneraluser',
      subcollection: 'public',
      title: 'General User',
      description: 'General user document for demonstration'
    },
    {
      id: 'SDSadminuser',
      path: 'tabbed_dict/public/SDSadminuser',
      subcollection: 'public',
      title: 'SDS Admin User',
      description: 'SDS admin user document for demonstration'
    },
    {
      id: 'SDSspecialuser',
      path: 'tabbed_dict/public/SDSspecialuser',
      subcollection: 'public',
      title: 'Special User',
      description: 'Special user document for demonstration'
    }
  ];

  // RTK Query hooks - use getTags query to get all document IDs
  const { data: documentIds = [], isLoading, refetch } = useGetTagsQuery(undefined, {
    skip: !shouldSearch,
  });

  // RTK Query mutations for CRUD operations

  // Console log the tags when data is received
  useEffect(() => {
    if (documentIds.length > 0) {
      console.log('🔥 Firestore Tags Query Results:', documentIds);
      console.log('📊 Total tags found:', documentIds.length);
      console.log('🏷️  Tags list:', documentIds.map(tag => tag.id));
    }
  }, [documentIds]);

  const loadSearchableListOnce = async () => {
    if (!isOpen || searchableListLoadStartedRef.current || isLoadingSearchableList) return;

    searchableListLoadStartedRef.current = true;
    setIsLoadingSearchableList(true);

    try {
      const response = await triggerGetSearchableList({ collectionName: 'tabbed_dict' }, true);
      const backendSearchableList = Array.isArray(response?.data) ? response.data : [];
      console.log('🔎 Backend searchableList:', backendSearchableList);
      setFirestoreSearchableList(backendSearchableList);
    } catch (error) {
      console.error('Error fetching backend searchable list:', error);
      setFirestoreSearchableList([]);
    } finally {
      setIsLoadingSearchableList(false);
    }
  };

  // Build suggestions list from documentIds
  useEffect(() => {
    const suggestions = [];
    const seen = new Set();

    const addSuggestion = (name, suggestion) => {
      if (!name) return;
      const normalizedName = String(name).trim();
      if (!normalizedName) return;
      const seenKey = normalizedName.toLowerCase();
      if (seen.has(seenKey)) return;
      seen.add(seenKey);
      suggestions.push({ name: normalizedName, ...suggestion });
    };

    // Existing suggestion behavior from documentIds
    documentIds.forEach((doc) => {
      const pathParts = doc.path?.split('/').filter(Boolean) || [];

      pathParts.forEach((part, index) => {
        addSuggestion(part, {
          type: index % 2 === 0 ? 'collection' : 'document',
          fullPath: pathParts.slice(0, index + 1).join('/'),
          level: index,
          originalDoc: doc
        });
      });

      addSuggestion(doc.id, {
        type: 'document',
        fullPath: doc.path,
        level: pathParts.length - 1,
        originalDoc: doc
      });

      addSuggestion(doc.subcollection, {
        type: 'subcollection',
        fullPath: doc.path,
        level: 1,
        originalDoc: doc
      });
    });

    // New suggestion behavior from FirestoreCollectionFetcher searchableList
    firestoreSearchableList.forEach((doc) => {
      const pathParts = doc.path?.split('/').filter(Boolean) || [];

      pathParts.forEach((part, index) => {
        addSuggestion(part, {
          type: index % 2 === 0 ? 'collection' : 'document',
          fullPath: pathParts.slice(0, index + 1).join('/'),
          level: index,
          originalDoc: doc
        });
      });

      addSuggestion(doc.id, {
        type: 'document',
        fullPath: doc.path,
        level: pathParts.length - 1,
        originalDoc: doc
      });

      Object.entries(doc).forEach(([fieldName, fieldValue]) => {
        if (['id', 'path'].includes(fieldName)) return;

        if (typeof fieldValue === 'string' || typeof fieldValue === 'number' || typeof fieldValue === 'boolean') {
          addSuggestion(String(fieldValue), {
            type: 'field',
            fullPath: doc.path,
            level: pathParts.length,
            originalDoc: doc
          });
        }
      });
    });

    setAllSuggestions((prevSuggestions) => {
      if (prevSuggestions.length === suggestions.length) {
        const isSame = prevSuggestions.every((prevItem, index) => {
          const nextItem = suggestions[index];
          return (
            prevItem?.name === nextItem?.name &&
            prevItem?.type === nextItem?.type &&
            prevItem?.fullPath === nextItem?.fullPath &&
            prevItem?.level === nextItem?.level
          );
        });

        if (isSame) {
          return prevSuggestions;
        }
      }

      return suggestions;
    });
    // console.log('📝 Built suggestions:', suggestions);
  }, [documentIds, firestoreSearchableList]);

  // Compute filtered suggestions based on search term
  const getFilteredSuggestions = () => {
    if (!searchTerm.trim() || !showSuggestions) {
      return [];
    }

    const searchLower = searchTerm.toLowerCase();
    return allSuggestions
      .filter((suggestion) => suggestion.name.toLowerCase().includes(searchLower))
      .slice(0, 10); // Limit to 10 suggestions
  };

  const resolveSuggestionTargetDocument = (suggestion) => {
    if (!suggestion) return null;

    const normalizePath = (value) => String(value || '').trim().replace(/^\/+|\/+$/g, '');
    const documentPool = Array.isArray(documentIds) ? documentIds : [];

    // Collection suggestions do not point to a concrete document payload.
    if (suggestion.type === 'collection') return null;

    const preferredPath = normalizePath(suggestion.originalDoc?.path || suggestion.fullPath);
    if (preferredPath) {
      const exactPathMatch = documentPool.find((doc) => normalizePath(doc?.path) === preferredPath);
      if (exactPathMatch) return exactPathMatch;

      // If we only have a deeper nested path, map it back to the nearest known document path.
      const nearestPathMatch = documentPool.find((doc) => preferredPath.startsWith(`${normalizePath(doc?.path)}/`));
      if (nearestPathMatch) return nearestPathMatch;
    }

    if (suggestion.originalDoc?.id && suggestion.originalDoc?.subcollection) {
      const scopedIdMatch = documentPool.find(
        (doc) => doc?.id === suggestion.originalDoc.id && doc?.subcollection === suggestion.originalDoc.subcollection
      );
      if (scopedIdMatch) return scopedIdMatch;
    }

    const fallbackId = suggestion.originalDoc?.id || suggestion.name;
    if (fallbackId) {
      const fallbackIdMatch = documentPool.find((doc) => doc?.id === fallbackId);
      if (fallbackIdMatch) return fallbackIdMatch;
    }

    return null;
  };

  // Expose component-specific window functions for console testing
  useEffect(() => {
    window.logFirestoreTags = () => {
      console.log('🔍 Triggering Firestore tags query via RTK Query...');
      setShouldSearch(true);
    };

    window.getCurrentTags = () => {
      console.log('📋 Current tags in RTK Query state:', documentIds);
      console.log('🔢 Current tags count:', documentIds.length);
      return documentIds;
    };

    // Cleanup function
    return () => {
      delete window.logFirestoreTags;
      delete window.getCurrentTags;
    };
  }, [documentIds]);

  // Reset search when modal closes, and trigger search when modal opens
  useEffect(() => {
    if (isOpen) {
      setShouldSearch(true);
    } else {
      setSearchTerm('');
      setShouldSearch(false);
      setIsLoadingSearchableList(false);
      setFirestoreSearchableList([]);
      searchableListLoadStartedRef.current = false;
      setShowSuggestions(false);
      setIsSearchActive(false);
      setFilteredResults([]);
    }
  }, [isOpen]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSuggestions && !e.target.closest('.search-input-container')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  // Handle search input changes
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim()) {
      setShowSuggestions(true);
      setIsSearchActive(false); // Reset search state when typing
    } else {
      setShowSuggestions(false);
      setIsSearchActive(false);
      setFilteredResults([]);
    }
  };

  const handleSearchInputFocus = () => {
    loadSearchableListOnce();
    if (searchTerm.trim()) {
      setShowSuggestions(true);
    }
  };

  const focusSearchInput = () => {
    if (!searchInputRef.current) return;
    searchInputRef.current.focus();
    const valueLength = searchInputRef.current.value?.length || 0;
    searchInputRef.current.setSelectionRange(valueLength, valueLength);
    handleSearchInputFocus();
  };

  const parseTabbedPath = (path) => {
    if (!path || typeof path !== 'string') {
      return { parentDocument: '', subcollection: '' };
    }

    const pathParts = path.split('/').filter(Boolean);
    const tabbedIdx = pathParts.indexOf('tabbed_dict');
    const parentDocument = tabbedIdx >= 0 ? (pathParts[tabbedIdx + 1] || '') : '';
    const subcollection = tabbedIdx >= 0 ? (pathParts[tabbedIdx + 2] || '') : '';

    return { parentDocument, subcollection };
  };

  const detectCrudMode = (entity) => {
    if (!entity || typeof entity !== 'object') {
      return 'DataCRUD';
    }

    const hasUserCrudFields = 'username' in entity || 'admin' in entity || 'editor' in entity;
    return hasUserCrudFields ? 'UserCRUD' : 'DataCRUD';
  };

  const buildCreatePrefillFromLayer = () => {
    const defaultState = { creationLevel: 'full' };

    if (currentLevel === 'document' && currentData?.document) {
      const currentDoc = currentData.document;
      const { parentDocument, subcollection } = parseTabbedPath(currentDoc.path || '');
      const layerCrudMode = parentDocument === 'users' ? 'UserCRUD' : 'DataCRUD';

      return {
        ...defaultState,
        crudMode: layerCrudMode,
        parentDocument: parentDocument || currentDoc.subcollection || '',
        subcollection: subcollection || currentDoc.id || '',
      };
    }

    if (currentLevel === 'user' && currentData?.user) {
      const currentUser = currentData.user;
      const { parentDocument, subcollection } = parseTabbedPath(currentData?.document?.path || '');
      const layerCrudMode = detectCrudMode(currentUser);

      const prefill = {
        ...defaultState,
        crudMode: layerCrudMode,
        parentDocument: parentDocument || currentData?.document?.subcollection || '',
        subcollection: subcollection || currentData?.document?.id || currentUser.subcollection || '',
      };

      if (layerCrudMode === 'UserCRUD') {
        return {
          ...prefill,
          username: currentUser.username || '',
          admin: Boolean(currentUser.admin),
          editor: Boolean(currentUser.editor),
        };
      }

      const customFields = Object.entries(currentUser)
        .filter(([key]) => !['uid', 'id', 'path', 'subcollection', 'created_at', 'updated_at', 'type', 'note'].includes(key))
        .map(([name, value]) => ({ name, value: String(value) }));

      return {
        ...prefill,
        customFields: customFields.length > 0 ? customFields : [{ name: '', value: '' }],
      };
    }

    return defaultState;
  };

  const openCreateForm = () => {
    setFormPopup({ isOpen: true, mode: 'create', document: buildCreatePrefillFromLayer() });
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.name);
    setShowSuggestions(false);

    const targetDoc = resolveSuggestionTargetDocument(suggestion);
    if (targetDoc?.id && targetDoc?.path) {
      handleDeepFetch(targetDoc);
      return;
    }

    executeSearch(suggestion.name);
  };

  // Execute search and filter results
  const executeSearch = (searchValue = searchTerm) => {
    if (!searchValue.trim()) {
      setIsSearchActive(false);
      setFilteredResults([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();

    // Filter documentIds based on search term
    const results = documentIds.filter((doc) => {
      const idMatch = doc.id?.toLowerCase().includes(searchLower);
      const pathMatch = doc.path?.toLowerCase().includes(searchLower);
      const subcollectionMatch = doc.subcollection?.toLowerCase().includes(searchLower);

      return idMatch || pathMatch || subcollectionMatch;
    });

    setFilteredResults(results);
    setIsSearchActive(true);
    setShowSuggestions(false);

    console.log('🔍 Search executed:', { searchValue, resultsCount: results.length });
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setIsSearchActive(false);
    setFilteredResults([]);
    setShowSuggestions(false);
  };

  // Handle enter key press to trigger search
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      executeSearch();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Handle keyboard shortcuts in search modal
  useEffect(() => {
    const isEditableTarget = (target) => {
      const element = target;
      if (!element || !element.tagName) return false;
      const tagName = element.tagName.toLowerCase();
      return (
        element.isContentEditable ||
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        Boolean(element.closest('input, textarea, select, [contenteditable="true"]'))
      );
    };

    const handleKeyboardShortcuts = (e) => {
      const key = e.key.toLowerCase();

      if (key === 'escape') {
        if (formPopup.isOpen) {
          // Close the document form popup
          setFormPopup({ isOpen: false, mode: 'create', document: null });
        } else if (isOpen) {
          // Close the main search modal
          onClose();
        }
        e.preventDefault();
        return;
      }

      if (!isOpen || formPopup.isOpen || isEditableTarget(e.target)) {
        return;
      }

      if (key === ' ') {
        focusSearchInput();
        e.preventDefault();
        return;
      }

      if (key === 'n') {
        openCreateForm();
        e.preventDefault();
      }
    };

    if (isOpen || formPopup.isOpen) {
      document.addEventListener('keydown', handleKeyboardShortcuts);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcuts);
    };
  }, [isOpen, formPopup.isOpen, onClose]);

  // CRUD handlers
  // Separate handler for user operations
  // Separate handler for user operations
  const handleUpdateDocument = (target, parentData) => {
    // If target is just a string, we can't proceed
    if (typeof target === 'string') {
      console.error('handleUpdateDocument called with string ID instead of object:', target);
      return;
    }

    // Extract parent document and subcollection from path
    let parentDocument = '';
    let subcollection = '';

    // Check if this is a user or a document
    if (target.uid) {
      // It's a user document
      // Parse from parentData.path or target.path
      const path = parentData?.path || target.path || '';
      const pathParts = path.split('/').filter(Boolean);

      if (pathParts.length >= 3) {
        // Path like "tabbed_dict/users/admin"
        parentDocument = pathParts[1]; // "users"
        subcollection = pathParts[2]; // "admin"
      }

      setFormPopup({
        isOpen: true,
        mode: 'update',
        document: {
          id: target.uid,
          parentDocument,
          subcollection,
          type: 'user',
          data: target
        }
      });
    } else {
      // It's a document
      // Try multiple sources for path and metadata
      const path = parentData?.path || target.path || '';
      const pathParts = path.split('/').filter(Boolean);

      // Try to extract from path
      if (pathParts.length >= 3) {
        parentDocument = pathParts[1];
        subcollection = pathParts[2];
      }

      // If not found in path, try from document properties
      if (!parentDocument && target.parentDocument) {
        parentDocument = target.parentDocument;
      }
      if (!subcollection && target.subcollection) {
        subcollection = target.subcollection;
      }

      setFormPopup({
        isOpen: true,
        mode: 'update',
        document: {
          id: target.id || target,
          parentDocument,
          subcollection,
          path: path,
          type: 'document',
          data: target
        }
      });
    }
  };

  const handleDeleteDocument = async (target, parentDocument) => {
    // Automatically determine what we're deleting based on target structure
    let deletionInfo = {};

    console.log('🗑️ Delete requested:', { target, parentDocument });

    // Check if it's a user document (has uid). parentDocument may be missing in tree view.
    if (target.uid) {
      // It's a user document - delete single user
      // ALWAYS use target.subcollection for the subcollection name
      const subcollectionName = target.subcollection || 'unknown';
      deletionInfo = {
        type: 'user',
        title: `Delete user "${target.username || maskUid(target.uid)}"?`,
        message: `This will permanently delete this user from "${subcollectionName}" subcollection.`,
        action: () => performUserDeletion(target, parentDocument || target)
      };
    }
    // Check if it's a subcollection (has subcollection field and path with 3+ parts)
    else if (target.subcollection && target.path) {
      const pathParts = target.path.split('/').filter(Boolean);
      if (pathParts.length >= 3) {
        // It's a subcollection - delete all documents inside
        // ALWAYS use target.subcollection for the subcollection name
        const subcollectionName = target.subcollection;
        deletionInfo = {
          type: 'subcollection',
          title: `Delete subcollection "${subcollectionName}"?`,
          message: `⚠️ This will delete ALL documents inside "${subcollectionName}" subcollection. This action cannot be undone!`,
          action: () => performSubcollectionDeletion(target)
        };
      } else {
        // It's a parent document - delete everything
        deletionInfo = {
          type: 'parent',
          title: `Delete document "${target.id}"?`,
          message: `⚠️ This will delete the document AND ALL its subcollections. This action cannot be undone!`,
          action: () => performParentDeletion(target)
        };
      }
    }
    // Otherwise it's a parent document
    else {
      const docId = target.id || target;
      deletionInfo = {
        type: 'parent',
        title: `Delete document "${docId}"?`,
        message: `⚠️ This will delete the document AND ALL its subcollections. This action cannot be undone!`,
        action: () => performParentDeletion(target)
      };
    }

    // Show confirmation toast
    toast(
      (t) => (
        <div className="text-center max-w-md">
          <p className="font-medium mb-2">{deletionInfo.title}</p>
          <p className="text-sm text-gray-600 mb-3">{deletionInfo.message}</p>
          <div className="flex gap-2 justify-center">
            <Button
              onClick={() => {
                toast.dismiss(t.id);
                deletionInfo.action();
              }}
              className="px-4 py-2 bg-red-500 text-white rounded text-sm font-medium hover:bg-red-600"
            >
              Delete
            </Button>
            <Button
              onClick={() => toast.dismiss(t.id)}
              className="px-4 py-2 bg-gray-500 text-white rounded text-sm font-medium hover:bg-gray-600"
            >
              Cancel
            </Button>
          </div>
        </div>
      ),
      {
        duration: 10000,
        position: 'top-center',
        style: { textAlign: 'center' }
      }
    );
  };

  const handleFormSubmit = async (formData) => {
    try {
      const { creationLevel, crudMode, parentDocument, subcollection, uid, admin, editor, username, description, customFields } = formData;

      if (formPopup.mode === 'create') {
        if (creationLevel === 'parent') {
          // Create parent document with a placeholder document in _init subcollection
          // Backend requires 'parent/subcollection' format
          // We'll create: tabbed_dict/{parentDocument}/_init/{generated-uuid}
          const documentsPath = `${parentDocument}/_init`;
          const generatedUid = uuidv4();

          await updateUidDict({
            collection: 'tabbed_dict',
            documents: documentsPath,
            uid: generatedUid,
            data: {
              description: description || `Parent document: ${parentDocument}`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              type: 'parent_init',
              note: 'This is a system placeholder to initialize the parent document structure'
            }
          }).unwrap();

          toast.success(`Parent document "${parentDocument}" created with structure`, {
            position: 'top-center',
            style: { textAlign: 'center' }
          });
        } else if (creationLevel === 'subcollection') {
          // Create subcollection with a placeholder document
          // Firestore requires at least one document in a subcollection
          // We'll create: tabbed_dict/{parentDocument}/{subcollection}/{generated-uuid}
          const documentsPath = `${parentDocument}/${subcollection}`;
          const generatedUid = uuidv4();

          await updateUidDict({
            collection: 'tabbed_dict',
            documents: documentsPath,
            uid: generatedUid,
            data: {
              description: description || `Subcollection: ${subcollection} under ${parentDocument}`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              type: 'subcollection_init',
              note: 'This is a system placeholder to initialize the subcollection'
            }
          }).unwrap();

          toast.success(`Subcollection "${subcollection}" created under "${parentDocument}"`, {
            position: 'top-center',
            style: { textAlign: 'center' }
          });
        } else if (creationLevel === 'full') {
          // Create full document with data
          // Path: tabbed_dict/{parentDocument}/{subcollection}/{uid}
          const documentsPath = `${parentDocument}/${subcollection}`;
          const finalUid = uid || uuidv4(); // Use provided UID or generate one

          let documentData = {};
          let successMessage = '';

          if (crudMode === 'UserCRUD') {
            // UserCRUD: Use predefined role fields
            documentData = {
              admin: admin,
              editor: editor,
              username: username
            };
            successMessage = `User "${username}" created successfully`;
          } else if (crudMode === 'DataCRUD') {
            // DataCRUD: Use custom fields
            documentData = {
              ...customFields,
              description: description || ''
            };
            successMessage = `Document created successfully in "${subcollection}"`;
          }

          // // Add metadata
          // documentData.created_at = new Date().toISOString();
          // documentData.updated_at = new Date().toISOString();

          await updateUidDict({
            collection: 'tabbed_dict',
            documents: documentsPath,
            uid: finalUid,
            data: documentData
          }).unwrap();

          toast.success(successMessage, {
            position: 'top-center',
            style: { textAlign: 'center' }
          });
        }
      } else {
        // Update existing document
        const documentsPath = `${parentDocument}/${subcollection}`;

        let documentData = {};
        let successMessage = '';

        if (crudMode === 'UserCRUD') {
          // UserCRUD: Update predefined role fields
          documentData = {
            admin: admin,
            editor: editor,
            username: username
          };
          successMessage = `User "${username}" updated successfully`;
        } else if (crudMode === 'DataCRUD') {
          // DataCRUD: Update custom fields
          documentData = {
            ...customFields,
            description: description || ''
          };
          successMessage = `Document updated successfully`;
        }

        // Add update timestamp
        documentData.updated_at = new Date().toISOString();

        await updateUidDict({
          collection: 'tabbed_dict',
          documents: documentsPath,
          uid: uid,
          data: documentData
        }).unwrap();

        toast.success(successMessage, {
          position: 'top-center',
          style: { textAlign: 'center' }
        });
      }

      // Refresh the search results
      setShouldSearch(true);

      // Trigger immediate refetch to get latest data
      await refetch();

      // If we're at document or user level, refetch that level's data too
      if (currentLevel === 'document' && currentData?.document) {
        const documentsPath = `${parentDocument}/${subcollection}`;
        const usersData = await triggerGetUsers({
          collection: 'tabbed_dict',
          documents: documentsPath,
        }).unwrap();

        setCurrentData(prev => ({
          ...prev,
          users: usersData || []
        }));
      } else if (currentLevel === 'user' && currentData?.user) {
        const documentsPath = `${parentDocument}/${subcollection}`;
        const usersData = await triggerGetUsers({
          collection: 'tabbed_dict',
          documents: documentsPath,
        }).unwrap();

        const updatedUser = usersData?.find(u => u.uid === uid);
        if (updatedUser) {
          setCurrentData(prev => ({
            ...prev,
            user: updatedUser
          }));
        }
      }

      // Clear search if active to show all results
      if (isSearchActive) {
        clearSearch();
      }
    } catch (error) {
      console.error('Failed to save document:', error);
      toast.error(`Failed to ${formPopup.mode} document: ${error.message || 'Unknown error'}`, {
        position: 'top-center',
        style: { textAlign: 'center' }
      });
    }
  };

  // const handleDeleteDocument = async (user, parentDocument) => {
  //   const userId = user.uid;
  //   const docId = parentDocument.id;
    
  //   // Show confirmation
  //   toast(
  //     (t) => (
  //       <div className="text-center">
  //         <p className="text-center">Delete user "{userId}" from "{docId}"?</p>
  //         <div className="flex gap-2 mt-2 justify-center">
  //           <button
  //             onClick={() => {
  //               toast.dismiss(t.id);
  //               performUserDeletion(user, parentDocument);
  //             }}
  //             className="px-3 py-1 bg-red-500 text-white rounded text-sm"
  //           >
  //             Delete
  //           </button>
  //           <button
  //             onClick={() => toast.dismiss(t.id)}
  //             className="px-3 py-1 bg-gray-500 text-white rounded text-sm"
  //           >
  //             Cancel
  //           </button>
  //         </div>
  //       </div>
  //     ),
  //     {
  //       duration: 5000,
  //       position: 'top-center',
  //       style: { textAlign: 'center' }
  //     }
  //   );
  // };

  const handleDeepFetch = async (doc) => {
    const { id, subcollection, path } = doc || {};

    setIsLoadingContent(true);
    setLoadingUsersMap(prev => ({...prev, [id]: true}));

    try {
      // Fetch users data for this document
      // For path structure: tabbed_dict/{parentDoc}/{subcollection}/{uid}
      // We need to query the subcollection that contains user documents

      // Parse the path to determine what to query
      // Path example: "tabbed_dict/users/admin" - we want to query the "admin" subcollection
      const pathParts = path?.split('/').filter(Boolean) || [];

      // Determine the documents parameter based on path structure
      let documentsParam;

      if (pathParts.length === 3) {
        // Path like "tabbed_dict/users/admin" - query the subcollection (admin) under parent (users)
        // Try passing as "users/admin" or just the subcollection name (id)
        documentsParam = `${subcollection || pathParts[1]}/${id}`;
      } else if (pathParts.length === 2) {
        // Path like "tabbed_dict/users" - query the document directly
        documentsParam = id;
      } else {
        // Fallback to original logic
        documentsParam = subcollection || id;
      }

      console.log('🔍 Fetching users with params:', {
        collection: 'tabbed_dict',
        documents: documentsParam,
        originalPath: path,
        id,
        subcollection
      });

      const usersData = await triggerGetUsers({
        collection: 'tabbed_dict',
        documents: documentsParam,
      }).unwrap();

      setDocumentUsersMap(prev => ({...prev, [id]: usersData}));
      
      // Set navigation state with complete data
      const documentData = {
        type: 'document',
        data: {
          document: doc,
          users: usersData,
          path: path || id,
          type: 'documentOverview'
        }
      };
      
      setNavigationStack([documentData]);
      setCurrentLevel('document');
      setCurrentData(documentData.data);
      
      console.log('🔍 Document loaded:', { id, path, users: usersData });
      toast.success(`Loaded document: ${id}`, {
        position: 'top-center',
        style: { textAlign: 'center' }
      });
    } catch (error) {
      console.error('Failed to fetch document:', error);
      toast.error('Failed to load document', {
        position: 'top-center',
        style: { textAlign: 'center' }
      });
    } finally {
      setIsLoadingContent(false);
      setLoadingUsersMap(prev => ({...prev, [id]: false}));
    }
  };
  
  const handleUserClick = (user, parentDocument) => {
    // Navigate to user level (which should show documents inside that user's collection)
    setCurrentLevel('user');
    const userData = {
      user,
      document: parentDocument,
      path: `${parentDocument.path}/${user.uid}`
    };
    setCurrentData(userData);
    
    // Add to navigation stack
    setNavigationStack(prev => [...prev, {
      type: 'user',
      data: userData
    }]);
    
    // Fetch documents inside this user's subcollection
    fetchUserDocuments(user, parentDocument);
  };
  
  // New function to fetch documents inside user's collection
  const fetchUserDocuments = async (user, parentDocument) => {
    setIsLoadingContent(true);
    try {
      // Build the collection path: tabbed_dict/{parentDoc}/{subcollection}/{uid}
      // Parse the path to get the parts
      const pathParts = parentDocument.path?.split('/') || [];

      // For path like "tabbed_dict/users/admin", we want to query documents under:
      // tabbed_dict/users/admin/{uid}/documents (if such a subcollection exists)
      const collectionRef = collection(
        db,
        'tabbed_dict',
        parentDocument.subcollection || pathParts[1] || 'users',
        parentDocument.id,
        user.uid
      );

      const snapshot = await getDocs(collectionRef);

      const docs = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        path: docSnap.ref.path,
        data: docSnap.data()
      }));

      setCurrentData(prev => ({
        ...prev,
        documents: docs,
        message: docs.length === 0 ? 'No documents found for this user' : null
      }));

      console.log('📄 User documents loaded:', {
        userUid: user.uid,
        documentCount: docs.length,
        path: collectionRef.path
      });
    } catch (error) {
      // console.error('Failed to fetch user documents:', error);
      setCurrentData(prev => ({
        ...prev,
        documents: [],
        error: error.message || 'Failed to load documents'
      }));
    } finally {
      setIsLoadingContent(false);
    }
  };
  
  const handleBackNavigation = () => {
    if (navigationStack.length === 0) {
      // Go back to document list
      setCurrentLevel('documents');
      setCurrentData(null);
      setNavigationStack([]);
    } else {
      // Remove current level from stack
      const newStack = [...navigationStack];
      newStack.pop();
      setNavigationStack(newStack);
      
      if (newStack.length === 0) {
        setCurrentLevel('documents');
        setCurrentData(null);
      } else {
        const prevLevel = newStack[newStack.length - 1];
        setCurrentLevel(prevLevel.type);
        setCurrentData(prevLevel.data); // This now contains complete data
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setShouldSearch(false);
      // Reset hierarchical navigation states
      setCurrentLevel('documents');
      setCurrentData(null);
      setNavigationStack([]);
      setIsLoadingContent(false);
    }
  }, [isOpen]);

  // const handleKeyPress = (e) => {
  //   if (e.key === 'Enter') {
  //     setShouldSearch(true);
  //   }
  // };
  // if (!isOpen) return null;

  const handleStartEditRoles = () => {
    setIsEditingRoles(true);

    const user = currentData?.user;
    // Detect document type based on fields
    const hasUserCRUDFields = 'username' in user || 'admin' in user || 'editor' in user;
    const docType = hasUserCRUDFields ? 'UserCRUD' : 'DataCRUD';
    setEditingUserLevelDocType(docType);

    if (docType === 'UserCRUD') {
      setEditedRoles({
        admin: user?.admin || false,
        editor: user?.editor || false,
        username: user?.username || ''
      });
    } else {
      // DataCRUD: Extract all fields except system fields
      const excludedFields = ['uid', 'id', 'path', 'subcollection', 'created_at', 'updated_at', 'type', 'note'];
      const docData = {};
      Object.keys(user).forEach(key => {
        if (!excludedFields.includes(key)) {
          docData[key] = user[key];
        }
      });
      setEditedUserLevelDocData(docData);
    }
  };

  const handleCancelEditRoles = () => {
    setIsEditingRoles(false);
    setEditingUserLevelDocType(null);
    setEditedRoles({ admin: false, editor: false, username: '' });
    setEditedUserLevelDocData({});
  };

  // Document level edit handlers (for user list)
  const handleStartEditUser = (user) => {
    setEditingUserId(user.uid);

    // Detect document type based on fields
    const hasUserCRUDFields = 'username' in user || 'admin' in user || 'editor' in user;
    const docType = hasUserCRUDFields ? 'UserCRUD' : 'DataCRUD';
    setEditingDocType(docType);

    if (docType === 'UserCRUD') {
      setEditedUserRoles({
        admin: user.admin || false,
        editor: user.editor || false,
        username: user.username || ''
      });
    } else {
      // DataCRUD: Extract all fields except system fields
      const excludedFields = ['uid', 'id', 'path', 'subcollection', 'created_at', 'updated_at', 'type', 'note'];
      const docData = {};
      Object.keys(user).forEach(key => {
        if (!excludedFields.includes(key)) {
          docData[key] = user[key];
        }
      });
      setEditedDocData(docData);
    }
  };

  const handleCancelEditUser = () => {
    setEditingUserId(null);
    setEditingDocType(null);
    setEditedUserRoles({ admin: false, editor: false, username: '' });
    setEditedDocData({});
  };

  const handleSaveUser = async (user) => {
    try {
      const { document } = currentData;

      // Construct the documents path
      let documentsPath;

      if (user.subcollection && document.subcollection) {
        documentsPath = `${document.subcollection}/${user.subcollection}`;
      } else if (document.path) {
        const pathParts = document.path.split('/').filter(Boolean);
        if (pathParts.length >= 3) {
          documentsPath = `${pathParts[1]}/${pathParts[2]}`;
        } else if (pathParts.length === 2 && user.subcollection) {
          documentsPath = `${pathParts[1]}/${user.subcollection}`;
        }
      }

      if (!documentsPath) {
        documentsPath = `${document.subcollection || 'users'}/${user.subcollection || document.id || 'admin'}`;
      }

      // Determine what data to save based on document type
      const dataToSave = editingDocType === 'UserCRUD' ? editedUserRoles : editedDocData;

      console.log('💾 Saving document with params:', {
        collection: 'tabbed_dict',
        documents: documentsPath,
        uid: user.uid,
        type: editingDocType,
        data: dataToSave
      });

      await updateUidDict({
        collection: 'tabbed_dict',
        documents: documentsPath,
        uid: user.uid,
        data: dataToSave
      }).unwrap();

      toast.success(`${editingDocType === 'UserCRUD' ? 'User' : 'Document'} updated successfully`, {
        position: 'top-center',
        style: { textAlign: 'center' }
      });

      // Refetch data at all levels
      await refetch();

      // Refetch current level data
      if (currentData?.document) {
        const usersData = await triggerGetUsers({
          collection: 'tabbed_dict',
          documents: documentsPath,
        }).unwrap();

        setCurrentData(prev => ({
          ...prev,
          users: usersData || []
        }));
      }

      setEditingUserId(null);
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user', {
        position: 'top-center',
        style: { textAlign: 'center' }
      });
    }
  };

  const handleSaveRoles = async () => {
    try {
      const { user, document } = currentData;

      // Construct the documents path in format "parent_doc/subcollection"
      // Try multiple approaches to get the correct path
      let documentsPath;

      console.log('🔍 Debug - Available data:', {
        user: user,
        document: document,
        userSubcollection: user.subcollection,
        documentPath: document.path,
        documentId: document.id,
        documentSubcollection: document.subcollection
      });

      // Approach 1: Use user.subcollection (which comes from the API response)
      if (user.subcollection && document.subcollection) {
        // user.subcollection = "admin", document.subcollection = "users"
        documentsPath = `${document.subcollection}/${user.subcollection}`;
      }
      // Approach 2: Parse from document path if it has 3+ parts
      else if (document.path) {
        const pathParts = document.path.split('/').filter(Boolean);
        if (pathParts.length >= 3) {
          // pathParts = ["tabbed_dict", "users", "admin"] → "users/admin"
          documentsPath = `${pathParts[1]}/${pathParts[2]}`;
        } else if (pathParts.length === 2 && user.subcollection) {
          // pathParts = ["tabbed_dict", "users"], user.subcollection = "admin" → "users/admin"
          documentsPath = `${pathParts[1]}/${user.subcollection}`;
        } else if (pathParts.length === 2 && document.id) {
          // Fallback: use document.id as subcollection
          documentsPath = `${pathParts[1]}/${document.id}`;
        }
      }
      // Approach 3: Fallback to manual construction
      if (!documentsPath) {
        documentsPath = `${document.subcollection || 'users'}/${user.subcollection || document.id || 'admin'}`;
      }

      // Determine what data to save based on document type
      const dataToSave = editingUserLevelDocType === 'UserCRUD' ? editedRoles : editedUserLevelDocData;

      console.log('💾 Saving document with params:', {
        collection: 'tabbed_dict',
        documents: documentsPath,
        uid: user.uid,
        type: editingUserLevelDocType,
        data: dataToSave
      });

      await updateUidDict({
        collection: 'tabbed_dict',
        documents: documentsPath,
        uid: user.uid,
        data: dataToSave
      }).unwrap();

      toast.success(`${editingUserLevelDocType === 'UserCRUD' ? 'User' : 'Document'} updated successfully`, {
        position: 'top-center',
        style: { textAlign: 'center' }
      });

      // Refetch data at all levels
      await refetch();

      // Refetch current level data to get latest from server
      const usersData = await triggerGetUsers({
        collection: 'tabbed_dict',
        documents: documentsPath,
      }).unwrap();

      // Find the updated user in the fresh data
      const updatedUser = usersData?.find(u => u.uid === user.uid);

      setCurrentData(prev => ({
        ...prev,
        user: updatedUser || { ...prev.user, ...dataToSave }
      }));

      setIsEditingRoles(false);
      setEditingUserLevelDocType(null);
    } catch (error) {
      console.error('Failed to update roles:', error);
      toast.error('Failed to update user roles', {
        position: 'top-center',
        style: { textAlign: 'center' }
      });
    }
  };

  const performUserDeletion = async (user, parentDocument) => {
    try {
      // Build documents path robustly from any available context.
      // Backend accepts collection paths (e.g. users/admin) and full doc paths.
      const normalizePath = (value) => (value || '').split('/').filter(Boolean);
      const toRelativeParts = (parts) => (parts[0] === 'tabbed_dict' ? parts.slice(1) : parts);
      const activeDocument = parentDocument || currentData?.document || {};

      const pathCandidates = [
        user?.path,
        activeDocument?.path,
        currentData?.path
      ].filter(Boolean);

      let documentsPath = '';

      for (const candidate of pathCandidates) {
        const parts = toRelativeParts(normalizePath(candidate));
        if (!parts.length) continue;

        // Candidate points to a document path: use its parent collection path.
        // We intentionally do not require the trailing doc id to equal user.uid,
        // because some documents store a separate `uid` field that differs from doc id.
        if (parts.length % 2 === 1 && parts.length >= 3) {
          documentsPath = parts.slice(0, -1).join('/');
          break;
        }

        // Candidate already points to a collection path.
        if (parts.length % 2 === 0 && parts.length >= 2) {
          documentsPath = parts.join('/');
          break;
        }
      }

      // Fallback using known metadata from current views.
      if (!documentsPath && activeDocument?.subcollection && user?.subcollection) {
        documentsPath = `${activeDocument.subcollection}/${user.subcollection}`;
      }

      // Common structure from your log:
      // parentDocument.path = "tabbed_dict/<parentDoc>", parentDocument.id = <parentDoc>,
      // and target.subcollection = <childSubcollection>.
      if (!documentsPath && activeDocument?.id && user?.subcollection) {
        documentsPath = `${activeDocument.id}/${user.subcollection}`;
      }

      if (!documentsPath && activeDocument?.subcollection && activeDocument?.id) {
        documentsPath = `${activeDocument.subcollection}/${activeDocument.id}`;
      }

      if (!documentsPath && currentData?.document?.subcollection && user?.subcollection) {
        documentsPath = `${currentData.document.subcollection}/${user.subcollection}`;
      }

      if (!documentsPath) {
        throw new Error(
          `Could not determine a valid documents path for user deletion (uid=${user?.uid || 'unknown'})`
        );
      }

      console.log('🗑️ Deleting user:', {
        collection: 'tabbed_dict',
        documents: documentsPath,
        uid: user.uid
      });

      // Delete user using backend API
      await deleteUidDict({
        collection: 'tabbed_dict',
        documents: documentsPath,
        uid: user.uid,
        delete_type: 'user'
      }).unwrap();

      toast.success(`User "${maskUid(user.uid)}" deleted successfully`, {
        position: 'top-center',
        style: { textAlign: 'center' }
      });

      // Refresh the current view
      if (currentLevel === 'document' && currentData?.document?.id === parentDocument.id) {
        await handleDeepFetch(currentData.document);
      } else {
        setShouldSearch(true);
      }
      await refetch();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(`Failed to delete user: ${error.message || 'Unknown error'}`, {
        position: 'top-center',
        style: { textAlign: 'center' }
      });
    }
  };

  const performSubcollectionDeletion = async (subcollectionDoc) => {
    const loadingToast = toast.loading('Deleting subcollection and all documents...', {
      position: 'top-center'
    });

    try {
      const pathParts = subcollectionDoc.path?.split('/').filter(Boolean) || [];
      let parentDoc, subcollectionName;

      if (pathParts.length >= 3) {
        parentDoc = pathParts[1];
        subcollectionName = pathParts[2];
      } else {
        parentDoc = subcollectionDoc.subcollection || pathParts[1];
        subcollectionName = subcollectionDoc.id;
      }

      console.log('🗑️ Deleting subcollection:', { parentDoc, subcollectionName });

      if (!parentDoc || !subcollectionName) {
        throw new Error('Could not determine parent document or subcollection name');
      }

      const documentsPath = `${parentDoc}/${subcollectionName}`;

      // Delete subcollection using backend API
      const result = await deleteUidDict({
        collection: 'tabbed_dict',
        documents: documentsPath,
        delete_type: 'subcollection'
      }).unwrap();

      toast.dismiss(loadingToast);
      toast.success(`Subcollection "${subcollectionName}" deleted (${result.deleted_count} documents)`, {
        position: 'top-center',
        style: { textAlign: 'center' }
      });

      setShouldSearch(true);
      await refetch();
      if (currentLevel !== 'documents') {
        handleBackNavigation();
      }
    } catch (error) {
      console.error('❌ Failed to delete subcollection:', error);
      toast.dismiss(loadingToast);
      toast.error(`Failed to delete subcollection: ${error.message || 'Unknown error'}`, {
        position: 'top-center',
        style: { textAlign: 'center' }
      });
    }
  };

  const performParentDeletion = async (parentDocInput) => {
    const loadingToast = toast.loading('Deleting parent document and all subcollections...', {
      position: 'top-center'
    });

    try {
      const pathParts = parentDocInput.path?.split('/').filter(Boolean) || [];
      const parentDocId = pathParts[1] || parentDocInput.id;

      console.log('🗑️ Deleting parent document:', parentDocId);

      if (!parentDocId) {
        throw new Error('Could not determine parent document ID');
      }

      // Delete parent using backend API
      const result = await deleteUidDict({
        collection: 'tabbed_dict',
        documents: parentDocId,
        delete_type: 'parent'
      }).unwrap();

      toast.dismiss(loadingToast);
      toast.success(
        `Parent "${parentDocId}" deleted (${result.deleted_count} documents, ${result.subcollections_deleted} subcollections)`,
        {
          position: 'top-center',
          style: { textAlign: 'center' },
          duration: 5000
        }
      );

      setShouldSearch(true);
      await refetch();
      if (currentLevel !== 'documents') {
        handleBackNavigation();
      }
    } catch (error) {
      console.error('❌ Failed to delete parent document:', error);
      toast.dismiss(loadingToast);
      toast.error(`Failed to delete parent: ${error.message || 'Unknown error'}`, {
        position: 'top-center',
        style: { textAlign: 'center' }
      });
    }
  };

  // Expose window function for console logging tags
  useEffect(() => {
    window.logFirestoreTags = () => {
      console.log('Triggering Firestore tags query...');
      setShouldSearch(true);
    };

    // Cleanup function
    return () => {
      delete window.logFirestoreTags;
    };
  }, []);

  if (!isOpen) return null;

  // Helper function to mask UID based on user role
const maskUid = (uid) => {
  if (!uid) return 'N/A';
  
  // If user is admin, show full UID
  if (isAdmin) {
    return uid;
  }
  
  // For non-admin users, mask most of the UID
  if (uid.length > 8) {
    const firstPart = uid.substring(0, 3);
    const lastPart = uid.substring(uid.length - 3);
    return `${firstPart}****${lastPart}`;
  }
  
  // For very short UIDs
  return '****';
};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="w-full max-w-2xl max-h-[80vh] rounded-lg shadow-xl overflow-hidden"
        style={{
          backgroundColor: theme.backgroundPrimary,
          color: theme.textPrimary
        }}
      >
        {/* Search Input */}
        <div className="px-6 py-4 border-b" style={{ borderColor: theme.borderColor }}>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 search-input-container">
              <Search
                size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                style={{ color: theme.textSecondary }}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Type to search documents and subcollections..."
                value={searchTerm}
                onChange={handleSearchInputChange}
                onKeyDown={handleKeyPress}
                onFocus={handleSearchInputFocus}
                className="w-full pl-10 pr-10 py-3 rounded-lg border focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: theme.backgroundSecondary,
                  borderColor: theme.borderColor,
                  color: theme.textPrimary,
                  '--tw-ring-color': theme.primary
                }}
                autoFocus
                title="Focus search (Space)"
              />
              {searchTerm && (
                <Button
                  onClick={clearSearch}
                  variant="subtle"
                  size="icon"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 rounded-full"
                  style={{ color: theme.textSecondary }}
                  title="Clear search"
                >
                  <X size={16} />
                </Button>
              )}

              {/* Suggestions Dropdown */}
              {showSuggestions && getFilteredSuggestions().length > 0 && (
                <div
                  className="absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-lg z-50 max-h-60 overflow-y-auto"
                  style={{
                    backgroundColor: theme.backgroundPrimary,
                    borderColor: theme.borderColor
                  }}
                >
                  {getFilteredSuggestions().map((suggestion, index) => (
                    <div
                      key={`${suggestion.name}-${index}`}
                      className="px-4 py-2 cursor-pointer hover:opacity-80 transition-opacity border-b last:border-b-0"
                      style={{
                        backgroundColor: theme.backgroundSecondary,
                        borderColor: theme.borderColor,
                        color: theme.textPrimary
                      }}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded flex items-center justify-center"
                          style={{
                            backgroundColor: suggestion.type === 'collection' ? theme.primary : 'transparent',
                            border: suggestion.type === 'document' ? `1px solid ${theme.primary}` : 'none',
                            color: suggestion.type === 'collection' ? '#fff' : theme.primary
                          }}
                        >
                          {suggestion.type === 'collection' ? <Database size={12} /> : <FileText size={12} />}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium">{suggestion.name}</div>
                          <div className="text-xs" style={{ color: theme.textSecondary }}>
                            {suggestion.type} • {suggestion.fullPath}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button
              onClick={() => executeSearch()}
              variant="primary"
              size="md"
              style={{
                backgroundColor: theme.primary,
                color: '#fff',
              }}
              title="Search (Enter)"
            >
              <Search size={16} />
            </Button>
            <Button
              onClick={openCreateForm}
              variant="primary"
              size="md"
              style={{
                backgroundColor: theme.primary,
                color: '#fff',
              }}
              title="Create new document (n)"
            >
              <Plus size={16} />
              <span className="text-sm">New</span>
            </Button>
            <Button
              onClick={onClose}
              variant="subtle"
              size="icon"
              style={{ color: theme.textSecondary }}
              title="Close (Esc)"
            >
              <X size={20} />
            </Button>
          </div>
        </div>

        {/* Results */}
        {/* Results */}
        <div className="flex-1 overflow-y-auto max-h-96">
          {/* Search Active Indicator */}
          {isSearchActive && currentLevel === 'documents' && (
            <div
              className="px-6 py-2 flex items-center justify-between text-sm border-b"
              style={{
                backgroundColor: theme.backgroundSecondary,
                borderColor: theme.borderColor,
                color: theme.textPrimary
              }}
            >
              <div className="flex items-center gap-2">
                <Search size={14} style={{ color: theme.primary }} />
                <span style={{ color: theme.textSecondary }}>
                  Searching for: <span style={{ color: theme.textPrimary, fontWeight: 'bold' }}>"{searchTerm}"</span>
                </span>
              </div>
              <Button
                onClick={clearSearch}
                variant="outline"
                size="sm"
                style={{
                  backgroundColor: theme.backgroundPrimary,
                  color: theme.primary,
                  border: `1px solid ${theme.borderColor}`
                }}
              >
                Clear
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2"
                  style={{ borderColor: theme.primary }}></div>
            </div>
          ) : currentLevel !== 'documents' ? (
            // Display current level content with navigation header
            <div>
              {/* Navigation Header */}
              <div className="flex items-center gap-3 px-6 py-3" style={{ 
                borderBottom: `1px solid ${theme.borderColor}`,
                backgroundColor: theme.backgroundSecondary 
              }}>
                <Button
                  onClick={handleBackNavigation}
                  variant="subtle"
                  size="md"
                  style={{ color: theme.textSecondary }}
                  title="Go back"
                >
                  <ArrowLeft size={16} />
                  <span className="text-sm">Back</span>
                </Button>
                <div className="text-sm font-medium truncate flex-1" style={{ color: theme.textSecondary }}>
                  {navigationStack.map((item, index) => {
                    const keyId = item.type === 'document'
                      ? `doc-${item.data.document.id}`
                      : item.type === 'user'
                      ? `user-${item.data.user.uid}`
                      : `data-${index}`;
                    return (
                      <span key={keyId}>
                        {index > 0 && ' > '}
                        {item.type === 'document' ? item.data.document.id :
                        item.type === 'user' ? item.data.user.username || maskUid(item.data.user.uid) :
                        maskUid(item.data.user?.uid) || 'Data'}
                      </span>
                    );
                  })}
                </div>
              </div>
              
              {/* Current Level Content */}
              {isLoadingContent ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2"
                      style={{ borderColor: theme.primary }}></div>
                  <span className="ml-3 text-sm" style={{ color: theme.textSecondary }}>
                    Loading...
                  </span>
                </div>
              ) : currentLevel === 'document' ? (
                // Document Level - Show subcollections (admin, nonadmin as users/ subcollections)
                <div className="divide-y overflow-y-auto max-h-96" style={{ borderColor: theme.borderColor }}>
                  <div className="px-6 py-4" style={{ backgroundColor: theme.backgroundSecondary }}>
                    <div className="font-medium text-sm mb-2" style={{ color: theme.textPrimary }}>
                      Document: {currentData?.document?.id}
                    </div>
                    <div className="text-xs mb-4" style={{ color: theme.textSecondary }}>
                      Path: {currentData?.document?.path || 'N/A'}
                    </div>
                    <div className="font-medium text-sm mb-3" style={{ color: theme.textPrimary }}>
                      User Subcollections ({currentData?.users?.length || 0}):
                    </div>
                    {currentData?.users?.map((user) => {
                      // Each user here represents a subcollection under users document
                      const userPath = `${currentData?.document?.path}/${user.subcollection}`;
                      const isEditing = editingUserId === user.uid;

                      return (
                        <div
                          key={user.uid}
                          className="mb-3 p-3 rounded-lg"
                          style={{
                            backgroundColor: theme.backgroundPrimary,
                            border: `1px solid ${theme.borderColor}`
                          }}
                        >
                          {!isEditing ? (
                            // Display Mode
                            <div
                              className="cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => handleUserClick(user, currentData.document)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: theme.primary, color: '#fff' }}
                                  >
                                    <User size={14} />
                                  </div>
                                  <div>
                                    <div className="font-medium text-sm" style={{ color: theme.textPrimary }}>
                                      DocId_UID: {maskUid(user.uid)}
                                    </div>
                                    <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                                      Path: {userPath}
                                    </div>
                                    {(user.admin !== undefined || user.editor !== undefined) ? (
                                      <div className="text-xs mt-1 flex gap-2" style={{ color: theme.textSecondary }}>
                                        <span>
                                          Admin: <span style={{
                                            color: user.admin ? '#10b981' : '#ef4444',
                                            fontWeight: 'bold'
                                          }}>
                                            {user.admin ? '✓' : '✗'}
                                          </span>
                                        </span>
                                        <span>
                                          Editor: <span style={{
                                            color: user.editor ? '#10b981' : '#ef4444',
                                            fontWeight: 'bold'
                                          }}>
                                            {user.editor ? '✓' : '✗'}
                                          </span>
                                        </span>
                                        {user.username && (
                                          <span>User: {user.username}</span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="text-xs mt-1 space-y-0.5" style={{ color: theme.textSecondary }}>
                                        {Object.entries(user)
                                          .filter(([key]) => !['uid', 'id', 'path', 'subcollection', 'created_at', 'updated_at', 'type', 'note'].includes(key))
                                          .map(([key, value]) => (
                                            <div key={key}>
                                              <span className="font-medium">{key}:</span>{' '}
                                              {key === 'photo_url' && typeof value === 'string' ? (
                                                <img
                                                  src={value}
                                                  alt="User photo"
                                                  className="inline-block rounded-full object-cover"
                                                  style={{ width: '40px', height: '40px', marginLeft: '8px', verticalAlign: 'middle' }}
                                                />
                                              ) : (
                                                String(value)
                                              )}
                                            </div>
                                          ))
                                        }
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <ChevronRight size={16} style={{ color: theme.textSecondary }} />
                                  {isAdmin && (
                                    <>
                                      <Button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStartEditUser(user);
                                        }}
                                        className="p-1.5 rounded hover:opacity-70 transition-opacity"
                                        style={{ color: theme.primary }}
                                        title="Edit user"
                                      >
                                        <Pencil size={14} />
                                      </Button>
                                      <Button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteDocument(user, currentData.document);
                                        }}
                                        className="p-1.5 rounded hover:opacity-70 transition-opacity"
                                        style={{ color: '#ef4444' }}
                                        title="Delete user"
                                      >
                                        <Trash2 size={14} />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Edit Mode
                            <div>
                              <div className="flex items-center gap-3 mb-3">
                                <div
                                  className="w-8 h-8 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: theme.primary, color: '#fff' }}
                                >
                                  <User size={14} />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-sm mb-2" style={{ color: theme.textPrimary }}>
                                    DocId_UID: {maskUid(user.uid)}
                                  </div>
                                  {/* Edit Fields */}
                                  {editingDocType === 'UserCRUD' ? (
                                    <div className="flex gap-3 items-center flex-wrap">
                                      <label className="flex items-center gap-2 text-xs" style={{ color: theme.textSecondary }}>
                                        <input
                                          type="checkbox"
                                          checked={editedUserRoles.admin}
                                          onChange={(e) => setEditedUserRoles({ ...editedUserRoles, admin: e.target.checked })}
                                          className="w-4 h-4"
                                        />
                                        Admin
                                      </label>
                                      <label className="flex items-center gap-2 text-xs" style={{ color: theme.textSecondary }}>
                                        <input
                                          type="checkbox"
                                          checked={editedUserRoles.editor}
                                          onChange={(e) => setEditedUserRoles({ ...editedUserRoles, editor: e.target.checked })}
                                          className="w-4 h-4"
                                        />
                                        Editor
                                      </label>
                                      <label className="flex items-center gap-2 text-xs" style={{ color: theme.textSecondary }}>
                                        Username:
                                        <input
                                          type="text"
                                          value={editedUserRoles.username}
                                          onChange={(e) => setEditedUserRoles({ ...editedUserRoles, username: e.target.value })}
                                          className="px-2 py-1 rounded border text-xs"
                                          style={{
                                            backgroundColor: theme.backgroundSecondary,
                                            borderColor: theme.borderColor,
                                            color: theme.textPrimary,
                                            minWidth: '100px'
                                          }}
                                          placeholder="Username"
                                        />
                                      </label>
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      {Object.entries(editedDocData).map(([key, value]) => (
                                        <div key={key} className="flex items-center gap-2">
                                          <label className="text-xs font-medium min-w-[80px]" style={{ color: theme.textSecondary }}>
                                            {key}:
                                          </label>
                                          <input
                                            type="text"
                                            value={String(value)}
                                            onChange={(e) => setEditedDocData({ ...editedDocData, [key]: e.target.value })}
                                            className="flex-1 px-2 py-1 rounded border text-xs"
                                            style={{
                                              backgroundColor: theme.backgroundSecondary,
                                              borderColor: theme.borderColor,
                                              color: theme.textPrimary
                                            }}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {/* Save/Cancel Buttons */}
                                <div className="flex items-center gap-1">
                                  <Button
                                    onClick={() => handleSaveUser(user)}
                                    className="px-2 py-1.5 rounded hover:opacity-80 transition-opacity"
                                    style={{ backgroundColor: '#10b981', color: '#fff' }}
                                    title="Save"
                                  >
                                    <Save size={14} />
                                  </Button>
                                  <Button
                                    onClick={handleCancelEditUser}
                                    className="px-2 py-1.5 rounded hover:opacity-80 transition-opacity"
                                    style={{ backgroundColor: '#6b7280', color: '#fff' }}
                                    title="Cancel"
                                  >
                                    <X size={14} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : currentLevel === 'user' ? (
                // User Level - Show documents inside this user's collection
                <div className="divide-y overflow-y-auto max-h-96" style={{ borderColor: theme.borderColor }}>
                  <div className="px-6 py-4" style={{ backgroundColor: theme.backgroundSecondary }}>
                    <div className="mb-4">
                      {/* User Info Row */}
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: theme.primary, color: '#fff' }}
                        >
                          <Database size={18} />
                        </div>
                        <div>
                          <div className="font-medium text-sm" style={{ color: theme.textPrimary }}>
                            DocId_UID: {maskUid(currentData?.user?.uid)}
                          </div>
                          <div className="text-xs" style={{ color: theme.textSecondary }}>
                            Path: {currentData?.document?.path}/{currentData?.user?.subcollection}
                          </div>
                        </div>
                      </div>

                      {/* Document Info Row */}
                      <div className="pl-12 py-2 rounded flex items-center justify-between" style={{ backgroundColor: theme.backgroundPrimary }}>
                        {!isEditingRoles ? (
                          // Display Mode
                          <>
                            {(currentData?.user?.admin !== undefined || currentData?.user?.editor !== undefined) ? (
                              <div className="font-medium text-sm flex gap-3" style={{ color: theme.textSecondary }}>
                                <span>
                                  Admin: <span style={{
                                    color: currentData.user.admin ? '#10b981' : '#ef4444',
                                    fontWeight: 'bold'
                                  }}>
                                    {currentData.user.admin ? '✓ True' : '✗ False'}
                                  </span>
                                </span>
                                <span>
                                  Editor: <span style={{
                                    color: currentData.user.editor ? '#10b981' : '#ef4444',
                                    fontWeight: 'bold'
                                  }}>
                                    {currentData.user.editor ? '✓ True' : '✗ False'}
                                  </span>
                                </span>
                                {currentData.user.username && (
                                  <span>
                                    Username: <span style={{ fontWeight: 'bold' }}>{currentData.user.username}</span>
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="font-medium text-sm space-y-1" style={{ color: theme.textSecondary }}>
                                {Object.entries(currentData?.user || {})
                                  .filter(([key]) => !['uid', 'id', 'path', 'subcollection', 'created_at', 'updated_at', 'type', 'note'].includes(key))
                                  .map(([key, value]) => (
                                    <div key={key}>
                                      <span className="font-medium">{key}:</span>{' '}
                                      {key === 'photo_url' && typeof value === 'string' ? (
                                        <img
                                          src={value}
                                          alt="User photo"
                                          className="inline-block rounded-full object-cover"
                                          style={{ width: '60px', height: '60px', marginLeft: '8px', verticalAlign: 'middle' }}
                                        />
                                      ) : (
                                        String(value)
                                      )}
                                    </div>
                                  ))
                                }
                              </div>
                            )}

                            {/* Action Buttons */}
                            {isAdmin && (
                              <div className="flex items-center gap-1">
                                <Button
                                  onClick={handleStartEditRoles}
                                  className="p-1.5 rounded hover:opacity-70 transition-opacity"
                                  style={{ color: theme.primary }}
                                  title="Edit document"
                                >
                                  <Pencil size={14} />
                                </Button>
                                <Button
                                  onClick={() => {
                                    handleDeleteDocument(currentData.user, currentData.document);
                                  }}
                                  className="p-1.5 rounded hover:opacity-70 transition-opacity"
                                  style={{ color: '#ef4444' }}
                                  title="Delete document"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            )}
                          </>
                        ) : (
                            // Edit Mode
                            <>
                              {editingUserLevelDocType === 'UserCRUD' ? (
                                <div className="font-medium text-sm flex gap-3 items-center" style={{ color: theme.textSecondary }}>
                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={editedRoles.admin}
                                      onChange={(e) => setEditedRoles({ ...editedRoles, admin: e.target.checked })}
                                      className="w-4 h-4"
                                    />
                                    Admin
                                  </label>
                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={editedRoles.editor}
                                      onChange={(e) => setEditedRoles({ ...editedRoles, editor: e.target.checked })}
                                      className="w-4 h-4"
                                    />
                                    Editor
                                  </label>
                                  <label className="flex items-center gap-2">
                                    Username:
                                    <input
                                      type="text"
                                      value={editedRoles.username}
                                      onChange={(e) => setEditedRoles({ ...editedRoles, username: e.target.value })}
                                      className="px-2 py-1 rounded border text-sm"
                                      style={{
                                        backgroundColor: theme.backgroundSecondary,
                                        borderColor: theme.borderColor,
                                        color: theme.textPrimary,
                                        minWidth: '120px'
                                      }}
                                      placeholder="Enter username"
                                    />
                                  </label>
                                </div>
                              ) : (
                                <div className="flex-1 space-y-2">
                                  {Object.entries(editedUserLevelDocData).map(([key, value]) => (
                                    <div key={key} className="flex items-center gap-2">
                                      <label className="text-xs font-medium min-w-[100px]" style={{ color: theme.textSecondary }}>
                                        {key}:
                                      </label>
                                      <input
                                        type="text"
                                        value={String(value)}
                                        onChange={(e) => setEditedUserLevelDocData({ ...editedUserLevelDocData, [key]: e.target.value })}
                                        className="flex-1 px-2 py-1 rounded border text-xs"
                                        style={{
                                          backgroundColor: theme.backgroundSecondary,
                                          borderColor: theme.borderColor,
                                          color: theme.textPrimary
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Save/Cancel Buttons */}
                              <div className="flex items-center gap-1">
                                <Button
                                  onClick={handleSaveRoles}
                                  className="px-3 py-1.5 rounded hover:opacity-80 transition-opacity text-sm font-medium"
                                  style={{ backgroundColor: '#10b981', color: '#fff' }}
                                  title="Save changes"
                                >
                                  <Save size={14} />
                                </Button>
                                <Button
                                  onClick={handleCancelEditRoles}
                                  className="px-3 py-1.5 rounded hover:opacity-80 transition-opacity text-sm font-medium"
                                  style={{ backgroundColor: '#6b7280', color: '#fff' }}
                                  title="Cancel"
                                >
                                  <X size={14} />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                    </div>
                  </div>
                </div>
              ) : currentLevel === 'userData' ? (
                // User Data Level - Show actual data
                <div className="divide-y overflow-y-auto max-h-96" style={{ borderColor: theme.borderColor, textAlign: 'justify' }}>
                  <div className="px-6 py-4" style={{ backgroundColor: theme.backgroundSecondary }}>
                    <div className="font-medium text-sm mb-3" style={{ color: theme.textPrimary }}>
                      Data for: {currentData?.user?.username || maskUid(currentData?.user?.uid)}
                    </div>
                    <div className="text-xs mb-4" style={{ color: theme.textSecondary }}>
                      Path: {currentData?.path || 'N/A'}
                    </div>
                    
                    {currentData?.error ? (
                      <div className="p-3 rounded" style={{ backgroundColor: '#ef444420', color: '#ef4444' }}>
                        Error: {currentData.error}
                      </div>
                    ) : currentData?.message ? (
                      <div className="p-3 rounded" style={{ backgroundColor: theme.backgroundPrimary, color: theme.textSecondary }}>
                        {currentData.message}
                      </div>
                    ) : currentData?.data ? (
                      <div className="font-mono text-sm whitespace-pre-wrap p-3 rounded" 
                          style={{ 
                            backgroundColor: theme.backgroundPrimary,
                            color: theme.textPrimary,
                            border: `1px solid ${theme.borderColor}`
                          }}>
                        {JSON.stringify(currentData.data, null, 2)}
                      </div>
                    ) : (
                      <div className="p-3 rounded" style={{ backgroundColor: theme.backgroundPrimary, color: theme.textSecondary }}>
                        No data available
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : isSearchActive && filteredResults.length === 0 ? (
            // No search results found
            <div className="px-6 py-8 text-center">
              <div style={{ color: theme.textSecondary }}>
                <Search size={48} className="mx-auto mb-3 opacity-50" />
                <div className="text-sm font-medium">No results found for "{searchTerm}"</div>
                <div className="text-xs mt-2">Try a different search term</div>
                <Button
                  onClick={clearSearch}
                  className="mt-4 px-4 py-2 rounded-lg text-sm transition-colors"
                  style={{
                    backgroundColor: theme.backgroundSecondary,
                    color: theme.textPrimary,
                    border: `1px solid ${theme.borderColor}`
                  }}
                >
                  Clear search
                </Button>
              </div>
            </div>
          ) : documentIds.length === 0 ? (
            // Sample documents (initial state)
            <div>
              <div className="px-6 py-3 text-sm font-medium" style={{ color: theme.textSecondary, borderBottom: `1px solid ${theme.borderColor}` }}>
                Sample Documents (Demo)
              </div>
              <div className="divide-y" style={{ borderColor: theme.borderColor }}>
                {sampleDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="px-6 py-4 hover:opacity-80 transition-opacity cursor-pointer"
                    style={{ backgroundColor: theme.backgroundSecondary }}
                    onClick={() => handleDeepFetch(doc)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: theme.primary, color: '#fff' }}
                      >
                        <FileText size={18} />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium" style={{ color: theme.textPrimary }}>
                          {doc.title}
                        </div>
                        <div className="text-sm" style={{ color: theme.textSecondary }}>
                          ID: {doc.id}
                        </div>
                        <div className="text-xs" style={{ color: theme.textSecondary }}>
                          {doc.description}
                        </div>
                        <div className="text-xs" style={{ color: theme.textSecondary }}>
                          Subcollection: {doc.subcollection}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateDocument(doc, { path: doc.path });
                          }}
                          className="p-2 rounded-lg hover:opacity-70 transition-opacity"
                          style={{ color: theme.primary }}
                          title="Update document"
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDocument(doc);
                          }}
                          className="p-2 rounded-lg hover:opacity-70 transition-opacity"
                          style={{ color: '#ef4444' }}
                          title="Delete document"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Document list (search results or all documents) - Improved hierarchical structure
            <div className="divide-y" style={{ borderColor: theme.borderColor }}>
              {/* Hierarchical Document Structure */}
              {(() => {
                // Use filtered results if search is active, otherwise use all documentIds
                const docsToDisplay = isSearchActive ? filteredResults : documentIds;

                // Create a nested structure for documents
                const documentTree = {};

                docsToDisplay.forEach((doc) => {
                  const pathParts = doc.path?.split('/') || [];
                  
                  // Build the tree structure
                  let currentLevel = documentTree;
                  let currentPath = '';
                  
                  for (let i = 0; i < pathParts.length; i++) {
                    const part = pathParts[i];
                    if (!part) continue;
                    
                    currentPath = currentPath ? `${currentPath}/${part}` : part;
                    
                    if (!currentLevel[part]) {
                      currentLevel[part] = {
                        id: part,
                        path: currentPath,
                        type: i % 2 === 0 ? 'collection' : 'document', // Even indices are collections, odd are documents
                        children: {},
                        documents: []
                      };
                    }
                    
                    if (i === pathParts.length - 1) {
                      // This is the final document
                      currentLevel[part].documents.push(doc);
                    }
                    
                    currentLevel = currentLevel[part].children;
                  }
                });
                
                // Helper function to render tree
                const renderTree = (node, level = 0) => {
                  const isCollection = node.type === 'collection';
                  const isDocument = node.type === 'document';
                  
                  return (
                    <div key={node.path} className="mb-1">
                      {/* Collection/Document header */}
                      <div
                        className={`px-6 py-3 flex items-center justify-between ${
                          isDocument ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
                        }`}
                        style={{
                          backgroundColor: level % 2 === 0 ? theme.backgroundSecondary : theme.backgroundPrimary,
                          paddingLeft: `${level * 1.5 + 1}rem`,
                          borderBottom: `1px solid ${theme.borderColor}`
                        }}
                        onClick={isDocument ? () => {
                          const targetDoc = node.documents?.[0];
                          if (targetDoc) handleDeepFetch(targetDoc);
                        } : undefined}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              isCollection ? '' : 'ring-2 ring-offset-1'
                            }`}
                            style={{ 
                              backgroundColor: isCollection ? theme.primary : 'transparent',
                              color: isCollection ? '#fff' : theme.textPrimary,
                              border: isDocument ? `2px solid ${theme.primary}` : 'none'
                            }}
                          >
                            {isCollection ? <Database size={16} /> : <FileText size={16} />}
                          </div>
                          <div>
                            <div className="font-medium text-sm" style={{ color: theme.textPrimary }}>
                              {isCollection ? `Collection: ${node.id}` : `Document: ${node.id}`}
                            </div>
                            {node.path && (
                              <div className="text-xs" style={{ color: theme.textSecondary }}>
                                Path: {node.path}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {isDocument && node.documents?.[0] && (
                          <div className="flex items-center gap-1">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateDocument(node.documents[0], { path: node.path });
                              }}
                              className="p-2 rounded-lg hover:opacity-70 transition-opacity"
                              style={{ color: theme.primary }}
                              title="Update document"
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDocument(node.documents[0]);
                              }}
                              className="p-2 rounded-lg hover:opacity-70 transition-opacity"
                              style={{ color: '#ef4444' }}
                              title="Delete document"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Render children */}
                      {Object.keys(node.children).length > 0 && (
                        <div className="ml-6">
                          {Object.values(node.children).map((child) => renderTree(child, level + 1))}
                        </div>
                      )}
                    </div>
                  );
                };
                
                // Render the tree starting from root level
                return Object.values(documentTree).map((node) => renderTree(node));
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t text-center"
          style={{ borderColor: theme.borderColor, color: theme.textSecondary }}
        >
          <div className="text-sm">
            {isSearchActive ? (
              <>
                Found {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''}
                {filteredResults.length > 0 && ` for "${searchTerm}"`}
              </>
            ) : documentIds.length > 0 ? (
              <>
                Found {documentIds.length} document{documentIds.length !== 1 ? 's' : ''}
                {shouldSearch && ' in tabbed_dict collection'}
              </>
            ) : (
              <>
                Showing {sampleDocuments.length} sample document{sampleDocuments.length !== 1 ? 's' : ''}
                {' (demo data)'}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Document Form Popup */}
      <DocumentForm
        isOpen={formPopup.isOpen}
        onClose={() => setFormPopup({ isOpen: false, mode: 'create', document: null })}
        onSubmit={handleFormSubmit}
        initialData={formPopup.document}
        theme={theme}
        mode={formPopup.mode}
      />
    </div>
  );
};

// // Expose to window for console testing
// if (typeof window !== 'undefined') {
//   window.testTabbedDictTagsQuery = testTabbedDictTagsQuery;
//   window.UsersList = UsersList;
// }

export default SearchBarModal;